class DateTimeFormatter {
    #locale;

    #timeZone;

    #shortFormatter;

    #fullDateFormatter;

    #fullFormatter;

    #shortWithWeekdayFormatter;

    #timeWithSecondsFormatter;

    constructor (locale = 'en', timeZone = undefined) {
        this.#locale = locale;
        this.#timeZone = timeZone;
        this.initializeFormatters(locale, timeZone);
    }

    initializeFormatters (locale, timeZone) {
        this.#shortFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone,
        });

        this.#fullDateFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone,
        });

        this.#fullFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
            timeZoneName: 'short',
            timeZone,
        });

        this.#shortWithWeekdayFormatter = new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone,
        });

        this.#timeWithSecondsFormatter = new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
            timeZone,
        });
    }

    get locale () {
        return this.#locale;
    }

    set locale (newLocale) {
        this.#locale = newLocale;
        this.initializeFormatters(newLocale, this.#timeZone);
    }

    get timeZone () {
        return this.#timeZone;
    }

    set timeZone (newTimeZone) {
        this.#timeZone = newTimeZone;
        this.initializeFormatters(this.#locale, newTimeZone);
    }

    formatFull ({ date, ignoreTimeZone = false }) {
        const formatter = ignoreTimeZone
            ? new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: true,
                timeZoneName: 'short',
            })
            : this.#fullFormatter;

        return formatter
            .formatToParts(date)
            .map((part, idx, parts) => {
                if (part.type === 'year' && parts[idx + 1]?.value.includes(',')) {
                    parts[idx + 1].value = ' ';
                }

                return part.value;
            })
            .join('');
    }

    formatShort ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        }

        return this.#shortFormatter.format(date);
    }

    formatFullDate ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }).format(date);
        }

        return this.#fullDateFormatter.format(date);
    }

    formatShortWithWeekday ({ date, ignoreTimeZone = false }) {
        let formatter = this.#shortWithWeekdayFormatter;
        if (ignoreTimeZone) {
            formatter = new Intl.DateTimeFormat(this.#locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        }

        if (this.#locale === 'ja') {
            const parts = formatter.formatToParts(date);

            return parts
                .map((part) => {
                    if (part.type === 'weekday') {
                        return `・${part.value}`;
                    }

                    return part.value;
                })
                .join('');
        }

        return formatter.format(date);
    }

    formatTimeWithSeconds ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: true,
            }).format(date);
        }

        return this.#timeWithSecondsFormatter.format(date);
    }

    formatBrowserTimezoneName ({ includeAbbr = false }) {
        const date = new Date();

        const longName = new Intl.DateTimeFormat(this.#locale, {
            timeZoneName: 'long',
        })
            .formatToParts(date)
            .map((part) => {
                if (part.type === 'timeZoneName') {
                    return part.value;
                }

                return '';
            })
            .join('');

        if (!includeAbbr) {
            return longName;
        }

        const abbr = new Intl.DateTimeFormat(this.#locale, {
            timeZoneName: 'short',
        })
            .formatToParts(date)
            .map((part) => {
                if (part.type === 'timeZoneName') {
                    return part.value;
                }

                return '';
            })
            .join('');

        return `${longName} (${abbr})`;
    }
}

const i18nDateTimeFormatter = new DateTimeFormatter();

export default i18nDateTimeFormatter;
